/* Cloth Bums cart drawer: one-tap top-ups and the dispatch countdown line. */
import { CartAddEvent, CartGroupedSections } from "@theme/events";

/* Top-ups: buttons carry data-cb-add='[{"id":123,"quantity":1}, ...]'. Adds through /cart/add.js with
   the cart sections, then fires the theme's CartAddEvent so the drawer and cart count re-render. */
document.addEventListener("click", async (event) => {
  const button = event.target instanceof Element ? event.target.closest("[data-cb-add]") : null;
  if (!button || button.disabled) return;
  event.preventDefault();

  let items;
  try {
    items = JSON.parse(button.dataset.cbAdd);
  } catch (error) {
    console.error("cb-cart: bad top-up data", error);
    return;
  }

  button.disabled = true;
  button.classList.add("is-loading");

  const sections = [];
  document.dispatchEvent(new CartGroupedSections(sections));

  try {
    const response = await fetch(window.FoxTheme.routes.cart_add_url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items,
        sections: [...new Set(sections)].join(","),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message);

    const quantity = items.reduce((total, item) => total + item.quantity, 0);
    document.dispatchEvent(
      new CartAddEvent({}, "cb-cart-topups", {
        source: "cb-cart-topups",
        itemCount: quantity,
        variantId: String(items[0].id),
        sections: data.sections,
      })
    );
  } catch (error) {
    console.error("cb-cart: couldn't add top-up", error);
    button.disabled = false;
    button.classList.remove("is-loading");
    button.classList.add("is-error");
  }
});

/* Dispatch line: Mon–Fri before the cutoff ships today, Mon–Thu after it ships tomorrow,
   Friday after the cutoff and weekends ship Monday. Same rules as the product page timer. */
class CbDispatchLine extends HTMLElement {
  connectedCallback() {
    this.text = this.querySelector(".cb-dispatch__text");
    this.cutoff = parseInt(this.dataset.cutoffHour || "13", 10);
    this.zone = this.dataset.timezone || "Australia/Brisbane";
    this.update();
    this.timer = setInterval(() => this.update(), 30000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  now() {
    const parts = new Intl.DateTimeFormat("en-AU", {
      timeZone: this.zone,
      weekday: "short",
      hour: "numeric",
      minute: "numeric",
      hourCycle: "h23",
    }).formatToParts(new Date());
    const get = (type) => parts.find((part) => part.type === type)?.value;
    const day = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(get("weekday"));
    return { day, hour: parseInt(get("hour"), 10), minute: parseInt(get("minute"), 10) };
  }

  update() {
    if (!this.text) return;
    const { day, hour, minute } = this.now();
    const weekday = day >= 1 && day <= 5;
    if (weekday && hour < this.cutoff) {
      const left = (this.cutoff - hour) * 60 - minute;
      const h = Math.floor(left / 60);
      const m = left % 60;
      this.text.innerHTML = `Order in <b>${h ? `${h}h ` : ""}${m}m</b> and it ships today`;
    } else if (day >= 1 && day <= 4) {
      this.text.innerHTML = "Order now and it ships <b>tomorrow</b>";
    } else {
      this.text.innerHTML = "Order now and it ships <b>first thing Monday</b>";
    }
  }
}

if (!customElements.get("cb-dispatch-line")) {
  customElements.define("cb-dispatch-line", CbDispatchLine);
}
